/**
 * Data layer for `abnormal-pattern-ml`. Thin typed fetch wrappers over /api/v1/abnormal-pattern-ml.
 * Raw `fetch()` is confined to this file per the dash contract.
 *
 * Backend is a deterministic feature-weighted scorer (see FS-448). Score is 0..100,
 * classification is one of normal|suspicious|abnormal. Items are keyed by event_id per tenant.
 */

export type AbnormalClassification = "normal" | "suspicious" | "abnormal";

export interface AbnormalPatternMlRow {
	id: number;
	tenant_id: string;
	event_id: string;
	source: string;
	features: unknown;
	score: number;
	classification: AbnormalClassification;
	model_version: string;
	reason: string;
	created_at: number;
}

export interface ListAbnormalPatternMlResponse {
	items: AbnormalPatternMlRow[];
}

const BASE = "/api/v1/abnormal-pattern-ml";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListAbnormalPatternMlResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListAbnormalPatternMlResponse>(res);
}

// ---------- data-layer logic (pure, unit-testable) --------------------------

/**
 * Sort events by descending anomaly score, stable on event_id so tests are deterministic.
 */
export function sortByScoreDesc<T extends { score: number; event_id: string }>(rows: T[]): T[] {
	return [...rows].sort((a, b) => {
		if (b.score !== a.score) return b.score - a.score;
		return a.event_id.localeCompare(b.event_id);
	});
}

/**
 * Bucket rows into score bands for the bar chart. Six 20-point buckets from 0..100 capturing
 * the breadth of low-score baseline traffic vs the abnormal long tail.
 */
export function bucketScores<T extends { score: number }>(
	rows: T[],
): Array<{ label: string; min: number; max: number; count: number }> {
	const buckets = [
		{ label: "0-19", min: 0, max: 19, count: 0 },
		{ label: "20-39", min: 20, max: 39, count: 0 },
		{ label: "40-59", min: 40, max: 59, count: 0 },
		{ label: "60-79", min: 60, max: 79, count: 0 },
		{ label: "80-100", min: 80, max: 100, count: 0 },
	];
	for (const r of rows) {
		const s = Math.max(0, Math.min(100, Math.round(r.score)));
		for (const b of buckets) {
			if (s >= b.min && s <= b.max) {
				b.count += 1;
				break;
			}
		}
	}
	return buckets;
}

/**
 * Filter to just the flagged entities — anything classified suspicious or abnormal.
 */
export function flaggedOnly<T extends { classification: AbnormalClassification }>(rows: T[]): T[] {
	return rows.filter((r) => r.classification !== "normal");
}

/**
 * Summary counts for the scorecards above the chart.
 */
export function summarize<T extends { classification: AbnormalClassification; score: number }>(
	rows: T[],
): { total: number; normal: number; suspicious: number; abnormal: number; avgScore: number } {
	let total = 0;
	let normal = 0;
	let suspicious = 0;
	let abnormal = 0;
	let scoreSum = 0;
	for (const r of rows) {
		total += 1;
		scoreSum += r.score;
		if (r.classification === "abnormal") abnormal += 1;
		else if (r.classification === "suspicious") suspicious += 1;
		else normal += 1;
	}
	const avgScore = total > 0 ? Math.round((scoreSum / total) * 10) / 10 : 0;
	return { total, normal, suspicious, abnormal, avgScore };
}

export const abnormalPatternMlQueryKeys = {
	all: ["abnormal-pattern-ml"] as const,
	list: () => [...abnormalPatternMlQueryKeys.all] as const,
	detail: (id: string) => [...abnormalPatternMlQueryKeys.all, id] as const,
};
